import { Course } from '@/models'

export interface GroupedCourses {
    title: string;
    sortKey: number;
    courses: Course[];
}

export function groupCoursesBySemester(
    courses: Course[] | undefined | null,
    globalCourseNames: Record<string, string>
): GroupedCourses[] {
    if (!courses) return [];

    const groupsMap: Record<string, GroupedCourses> = {};

    courses.forEach(course => {
        const isGlobal = globalCourseNames.hasOwnProperty(course.name);
        if (isGlobal) {
            // Global courses are rendered separately at the top of the list,
            // not within any semester group.
            return;
        }

        const match = course.name.match(/\.(\d)(\d{2})(\d)$/);
        if (match) {
            const year = match[2]; // e.g. '26'
            const termCode = match[3]; // '1', '4', '7'
            
            const fullYear = `20${year}`;
            let term = '';
            let termSort = 0;
            if (termCode === '1') { 
                term = 'Winter'; 
                termSort = 1; 
            } else if (termCode === '4') { 
                term = 'Summer'; 
                termSort = 2; 
            } else if (termCode === '7') { 
                term = 'Fall'; 
                termSort = 3; 
            } else { 
                term = 'Term ' + termCode; 
                termSort = parseInt(termCode); 
            }

            const groupTitle = `${term} ${fullYear}`;
            const sortKey = parseInt(`${fullYear}${termSort}`);

            if (!groupsMap[groupTitle]) {
                groupsMap[groupTitle] = { title: groupTitle, sortKey, courses: [] };
            }
            groupsMap[groupTitle].courses.push(course);
        } else {
            if (!groupsMap['General']) {
                groupsMap['General'] = { title: 'No Semester', sortKey: -1, courses: [] };
            }
            groupsMap['General'].courses.push(course);
        }
    });

    // Sort groups descending by sortKey (so General with -1 is always last)
    const sortedGroups = Object.values(groupsMap).sort((a, b) => b.sortKey - a.sortKey);

    // Sort courses within each group alphabetically
    sortedGroups.forEach(group => {
        group.courses.sort((a, b) => {
            const aName = globalCourseNames[a.name] || a.name.replace(/\.\d{4}$/, '');
            const bName = globalCourseNames[b.name] || b.name.replace(/\.\d{4}$/, '');
            return aName.localeCompare(bName);
        });
    });

    return sortedGroups;
}

export function getGlobalCourses(
    courses: Course[] | undefined | null,
    globalCourseNames: Record<string, string>
): Course[] {
    if (!courses) return [];

    return courses
        .filter(course => globalCourseNames.hasOwnProperty(course.name))
        .sort((a, b) => {
            const aName = globalCourseNames[a.name] || a.name;
            const bName = globalCourseNames[b.name] || b.name;
            return aName.localeCompare(bName);
        });
}

export function formatCourseDisplayName(courseName: string, globalCourseNames: Record<string, string>): string {
    return globalCourseNames[courseName] || courseName.replace(/\.\d{4}$/, '');
}
